import React, { useState } from 'react';

// Shared Invite Collaborator Modal Component
// Can be used in both Dashboard (with project selector) and CommunicationCentre (pre-selected project)
const InviteCollaboratorModal = ({
  // Project selection (optional - if not provided, uses defaultProjectId)
  projects = null, // Array of projects for selector (null = hide selector)
  selectedProjectId,
  setSelectedProjectId,
  defaultProjectId = null, // Pre-selected project ID (for CommunicationCentre)
  
  // Form state
  inviteMode, setInviteMode,
  inviteName, setInviteName,
  inviteEmail, setInviteEmail,
  invitePhone, setInvitePhone,
  notificationMethod, setNotificationMethod,
  inviteType, setInviteType,
  tradeCategory, setTradeCategory,
  messageVisibility, setMessageVisibility,
  scheduleAccess, setScheduleAccess,
  
  // Search state
  searchQuery, setSearchQuery,
  searchLocation, setSearchLocation,
  searchType, setSearchType,
  searchResults, searching,
  designerLocation,
  
  // Callbacks
  onSearch, onInviteFromSearch, onSubmit, onClose, onAddBusiness
}) => {
  const [showAddBusinessModal, setShowAddBusinessModal] = useState(false);
  const [newBusiness, setNewBusiness] = useState({
    name: '',
    businessType: '',
    address: '',
    city: '',
    provinceState: '',
    phone: '',
    email: '',
    website: ''
  });
  // Use defaultProjectId if no selectedProjectId and projects selector is hidden
  const effectiveProjectId = projects ? selectedProjectId : (selectedProjectId || defaultProjectId);
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Invite Collaborator</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Mode Tabs */}
        <div className="flex gap-2 mb-4 border-b">
          <button
            type="button"
            onClick={() => setInviteMode('manual')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              inviteMode === 'manual'
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Manual Entry
          </button>
          <button
            type="button"
            onClick={() => setInviteMode('search')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              inviteMode === 'search'
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Search Businesses
          </button>
        </div>

        {/* Manual Entry Form */}
        {inviteMode === 'manual' && (
          <form onSubmit={(e) => {
            e.preventDefault();
            onSubmit(effectiveProjectId);
          }}>
            {/* Project Selector - only show if projects array is provided */}
            {projects && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedProjectId || ''}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">Select a project...</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.project_name || project.name || `Project ${project.id}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Email helper banner if email was pre-filled */}
            {inviteEmail && inviteName && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-blue-800 font-medium">✓ Pre-filled from search: {inviteName}</p>
                    <p className="text-xs text-blue-600 mt-1">Please verify the email address before sending</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="client"
                    checked={inviteType === 'client'}
                    onChange={(e) => setInviteType(e.target.value)}
                    className="mr-2"
                  />
                  Client
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="trade"
                    checked={inviteType === 'trade'}
                    onChange={(e) => setInviteType(e.target.value)}
                    className="mr-2"
                  />
                  Trade/Supplier
                </label>
              </div>
            </div>

            {/* Trade Category - only show when trade is selected */}
            {inviteType === 'trade' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-gray-500 text-xs">(optional)</span>
                </label>
                <select
                  value={tradeCategory}
                  onChange={(e) => setTradeCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <optgroup label="General">
                    <option value="contractor">General Contractor</option>
                    <option value="supplier">Supplier</option>
                    <option value="installer">Installer</option>
                    <option value="consultant">Consultant</option>
                  </optgroup>
                  <optgroup label="Landscaping & Design">
                    <option value="landscaper">Landscaper</option>
                    <option value="landscape designer">Landscape Designer</option>
                    <option value="landscape architect">Landscape Architect</option>
                    <option value="garden designer">Garden Designer</option>
                    <option value="hardscape">Hardscape</option>
                    <option value="outdoor living">Outdoor Living</option>
                  </optgroup>
                  <optgroup label="Installation & Construction">
                    <option value="irrigation">Irrigation</option>
                    <option value="sprinkler">Sprinkler Installation</option>
                    <option value="drainage">Drainage Contractor</option>
                    <option value="fencing">Fencing Contractor</option>
                    <option value="deck builder">Deck Builder</option>
                    <option value="patio">Patio Contractor</option>
                    <option value="retaining wall">Retaining Wall</option>
                    <option value="paving">Paving Contractor</option>
                    <option value="concrete">Concrete Contractor</option>
                  </optgroup>
                  <optgroup label="Tree & Plant Services">
                    <option value="tree service">Tree Service</option>
                    <option value="arborist">Arborist</option>
                    <option value="tree removal">Tree Removal</option>
                    <option value="tree trimming">Tree Trimming</option>
                    <option value="stump removal">Stump Removal</option>
                    <option value="nursery">Nursery</option>
                    <option value="garden center">Garden Center</option>
                  </optgroup>
                  <optgroup label="Maintenance & Care">
                    <option value="lawn care">Lawn Care</option>
                    <option value="lawn mowing">Lawn Mowing</option>
                    <option value="landscape maintenance">Landscape Maintenance</option>
                    <option value="garden maintenance">Garden Maintenance</option>
                    <option value="fertilizer">Fertilizer Service</option>
                    <option value="pest control">Pest Control</option>
                  </optgroup>
                  <optgroup label="Suppliers & Retail">
                    <option value="nursery">Plant Nursery</option>
                    <option value="garden supply">Garden Supply</option>
                    <option value="mulch">Mulch Supplier</option>
                    <option value="stone">Stone Supplier</option>
                    <option value="gravel">Gravel Supplier</option>
                    <option value="soil">Soil Supplier</option>
                  </optgroup>
                  <optgroup label="Specialty Services">
                    <option value="lighting">Outdoor Lighting</option>
                    <option value="water feature">Water Feature</option>
                    <option value="pond">Pond Installation</option>
                    <option value="fire pit">Fire Pit Installation</option>
                    <option value="outdoor kitchen">Outdoor Kitchen</option>
                    <option value="pool">Pool Contractor</option>
                    <option value="snow removal">Snow Removal</option>
                  </optgroup>
                  <optgroup label="Other Services">
                    <option value="excavation">Excavation</option>
                    <option value="grading">Grading</option>
                    <option value="seeding">Lawn Seeding</option>
                    <option value="sod">Sod Installation</option>
                    <option value="roofing">Roofing</option>
                    <option value="plumbing">Plumbing</option>
                    <option value="electrical">Electrical</option>
                    <option value="painting">Painting</option>
                    <option value="carpentry">Carpentry</option>
                    <option value="masonry">Masonry</option>
                  </optgroup>
                </select>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name
              </label>
              <input
                type="text"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="Enter name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="email@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number <span className="text-gray-500 text-xs">(optional, for SMS notifications)</span>
              </label>
              <input
                type="tel"
                value={invitePhone || ''}
                onChange={(e) => setInvitePhone(e.target.value)}
                placeholder="+1234567890"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Include country code (e.g., +1 for US/Canada)
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notification Method
              </label>
              <p className="text-xs text-gray-500 mb-2">
                How should this person receive message notifications?
              </p>
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="email"
                    checked={notificationMethod === 'email' || !notificationMethod}
                    onChange={(e) => setNotificationMethod(e.target.value)}
                    className="mt-1"
                  />
                  <div>
                    <span className="text-sm font-medium">Email</span>
                    <p className="text-xs text-gray-500">Receive notifications via email</p>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="sms"
                    checked={notificationMethod === 'sms'}
                    onChange={(e) => setNotificationMethod(e.target.value)}
                    className="mt-1"
                  />
                  <div>
                    <span className="text-sm font-medium">SMS</span>
                    <p className="text-xs text-gray-500">Receive notifications via text message (requires phone number)</p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      Coming soon — we’ll save the phone number and preference for later.
                    </p>
                  </div>
                </label>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Visibility
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Control what messages this person can see when they join
              </p>
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="all"
                    checked={messageVisibility === 'all'}
                    onChange={(e) => setMessageVisibility(e.target.value)}
                    className="mt-1"
                  />
                  <div>
                    <span className="text-sm font-medium">All Messages</span>
                    <p className="text-xs text-gray-500">Can see all past and future messages in the project</p>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="selected"
                    checked={messageVisibility === 'selected'}
                    onChange={(e) => setMessageVisibility(e.target.value)}
                    className="mt-1"
                  />
                  <div>
                    <span className="text-sm font-medium">Selected Conversations</span>
                    <p className="text-xs text-gray-500">Can only see messages you specifically grant access to</p>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="none"
                    checked={messageVisibility === 'none'}
                    onChange={(e) => setMessageVisibility(e.target.value)}
                    className="mt-1"
                  />
                  <div>
                    <span className="text-sm font-medium">No Past Messages</span>
                    <p className="text-xs text-gray-500">Can only see new messages sent after they join</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Schedule Access (only for trades) */}
            {inviteType === 'trade' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Schedule Access
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={scheduleAccess}
                    onChange={(e) => setScheduleAccess(e.target.checked)}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700">
                    Allow access to project schedule/calendar
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {scheduleAccess ? 'Can view and interact with the project schedule' : 'Cannot access the schedule tab'}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                Send Invitation
              </button>
            </div>
          </form>
        )}

        {/* Search Businesses Tab */}
        {inviteMode === 'search' && (
          <div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Name or Type
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="e.g., 'landscaper' or 'ABC Landscaping'"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location (optional)
              </label>
              <input
                type="text"
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
                placeholder="City, Province/State, or postal code"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Type
              </label>
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="all">All Types</option>
                <option value="landscaper">Landscaper</option>
                <option value="contractor">Contractor</option>
                <option value="nursery">Nursery</option>
                <option value="supplier">Supplier</option>
              </select>
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (onSearch) {
                  onSearch(e);
                }
              }}
              disabled={searching || !searchQuery.trim()}
              className="w-full mb-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {searching ? 'Searching...' : 'Search Businesses'}
            </button>

            {searchResults.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-60 overflow-y-auto">
                  {searchResults.map((business, index) => (
                    <div
                      key={index}
                      onClick={() => onInviteFromSearch(business)}
                      className="p-3 hover:bg-green-50 cursor-pointer border-b last:border-b-0"
                    >
                      <div className="font-semibold text-gray-900">{business.name}</div>
                      {business.address && (
                        <div className="text-sm text-gray-600">{business.address}</div>
                      )}
                      {business.phone && (
                        <div className="text-xs text-gray-500">📞 {business.phone}</div>
                      )}
                      {business.website && (
                        <div className="text-xs text-blue-600">🌐 {business.website}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {searchResults.length === 0 && !searching && searchQuery && (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500 mb-3">
                  No businesses found. Try a different search term.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setNewBusiness({
                      ...newBusiness,
                      name: searchQuery
                    });
                    setShowAddBusinessModal(true);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                >
                  + Add "{searchQuery}" Manually
                </button>
              </div>
            )}

            {/* Add Business Button - always show when in search mode */}
            <div className="mt-4 pt-4 border-t">
              <button
                type="button"
                onClick={() => {
                  setNewBusiness({
                    name: searchQuery || '',
                    businessType: searchType !== 'all' ? searchType : '',
                    city: searchLocation || '',
                    address: '',
                    provinceState: '',
                    phone: '',
                    email: '',
                    website: ''
                  });
                  setShowAddBusinessModal(true);
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Business Not Listed
              </button>
            </div>
          </div>
        )}

        {/* Add Business Modal */}
        {showAddBusinessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Add Business Manually</h3>
                <button
                  onClick={() => {
                    setShowAddBusinessModal(false);
                    setNewBusiness({
                      name: '',
                      businessType: '',
                      address: '',
                      city: '',
                      provinceState: '',
                      phone: '',
                      email: '',
                      website: ''
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!newBusiness.name.trim()) {
                  alert('Business name is required');
                  return;
                }

                try {
                  if (onAddBusiness) {
                    await onAddBusiness(newBusiness);
                    setShowAddBusinessModal(false);
                    setNewBusiness({
                      name: '',
                      businessType: '',
                      address: '',
                      city: '',
                      provinceState: '',
                      phone: '',
                      email: '',
                      website: ''
                    });
                  }
                } catch (error) {
                  console.error('Error adding business:', error);
                }
              }}>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      value={newBusiness.name}
                      onChange={(e) => setNewBusiness({ ...newBusiness, name: e.target.value })}
                      placeholder="e.g., Aspen Ridge Design"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Type
                    </label>
                    <input
                      type="text"
                      value={newBusiness.businessType}
                      onChange={(e) => setNewBusiness({ ...newBusiness, businessType: e.target.value })}
                      placeholder="e.g., Landscape Designer"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <input
                      type="text"
                      value={newBusiness.address}
                      onChange={(e) => setNewBusiness({ ...newBusiness, address: e.target.value })}
                      placeholder="Street address"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        value={newBusiness.city}
                        onChange={(e) => setNewBusiness({ ...newBusiness, city: e.target.value })}
                        placeholder="City"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Province/State
                      </label>
                      <input
                        type="text"
                        value={newBusiness.provinceState}
                        onChange={(e) => setNewBusiness({ ...newBusiness, provinceState: e.target.value })}
                        placeholder="BC"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={newBusiness.phone}
                        onChange={(e) => setNewBusiness({ ...newBusiness, phone: e.target.value })}
                        placeholder="(250) 123-4567"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={newBusiness.email}
                        onChange={(e) => setNewBusiness({ ...newBusiness, email: e.target.value })}
                        placeholder="contact@business.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Website
                    </label>
                    <input
                      type="url"
                      value={newBusiness.website}
                      onChange={(e) => setNewBusiness({ ...newBusiness, website: e.target.value })}
                      placeholder="https://www.business.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddBusinessModal(false);
                      setNewBusiness({
                        name: '',
                        businessType: '',
                        address: '',
                        city: '',
                        provinceState: '',
                        phone: '',
                        email: '',
                        website: ''
                      });
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                  >
                    Add Business
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteCollaboratorModal;

